'use client';

import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useInView } from 'framer-motion';
import { useAnimation } from '@/context/AnimationContext';

const DigitColumn = ({ digit, isInView, delay, shouldRunAnimations, isMobile }) => {
    const isNumber = !isNaN(parseInt(digit));

    if (!isNumber) {
        return <span>{digit}</span>;
    }

    const animateTarget = isInView && (isMobile || shouldRunAnimations)
        ? { y: `-${parseInt(digit) * 10}%` }
        : { y: 0 };

    // On mobile, we might want to just show the final number static, or allow the scroll animation if desired.
    // Requirement says "do not add animations" for mobile.
    // So on mobile we should probably just show the final state immediately without transition?
    // Or just set duration to 0. 
    // Let's set the initial state to the final value if isMobile.

    // However, existing logic uses mapping 0-9. To show the digit static, we align the column.
    // If isMobile, we set initial y to target.

    const initialY = isMobile ? `-${parseInt(digit) * 10}%` : 0;

    return (
        <span className="inline-flex flex-col h-[1.1em] overflow-hidden">
            <motion.span
                initial={{ y: initialY }}
                animate={isMobile ? { y: initialY } : animateTarget}
                transition={isMobile ? { duration: 0 } : {
                    duration: 2,
                    delay: delay,
                    ease: [0.16, 1, 0.3, 1]
                }}
                className="flex flex-col"
            >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <span key={num} className="h-full flex items-center justify-center leading-none">
                        {num}
                    </span>
                ))}
            </motion.span>
        </span>
    );
};

const AnimatedNumber = ({ value, delay = 0, shouldRunAnimations, isMobile }) => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true, margin: "-50px" });
    const characters = value.split('');

    return (
        <span ref={containerRef} className="inline-flex items-baseline whitespace-pre">
            {characters.map((char, index) => (
                <DigitColumn
                    key={index}
                    digit={char}
                    isInView={isInView}
                    delay={delay + (index * 0.05)}
                    shouldRunAnimations={shouldRunAnimations}
                    isMobile={isMobile}
                />
            ))}
        </span>
    );
};

const stats = [
    {
        value: "70%+",
        label: "hiring decisions are influenced by clear communication and well-presented resumes"
    },
    {
        value: "3× faster",
        label: "professionals improve interview confidence with structured practice and feedback"
    },
    {
        value: "2× more likely",
        label: "to stay consistent and progress faster with guided learning and mentorship"
    }
];

const CareerStats = () => {
    const { isMobile, shouldAnimate } = useAnimation();
    const shouldRunAnimations = !isMobile && shouldAnimate;

    return (
        <section className="container relative z-20">
            <motion.div
                initial={isMobile ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                whileInView={isMobile || shouldRunAnimations ? { opacity: 1, scale: 1 } : undefined}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="relative group"
            >
                {/* Background Glow Effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-500 to-secondary-500 rounded-3xl blur opacity-10 group-hover:opacity-20 transition duration-1000 group-hover:duration-200" />

                <div className="relative bg-[#0B0F19] dark:bg-gray-950 rounded-3xl p-8 lg:p-12 shadow-2xl border border-white/10 dark:border-white/5 backdrop-blur-xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 text-white">
                        {stats.map((stat, index) => (
                            <div key={index} className="flex flex-col gap-3 relative">
                                <h3 className="text-4xl lg:text-5xl font-extrabold tracking-tighter text-white mb-1">
                                    <AnimatedNumber value={stat.value} delay={0.5 + index * 0.1} shouldRunAnimations={shouldRunAnimations} isMobile={isMobile} />
                                </h3>
                                <p className="text-gray-200 text-sm lg:text-base leading-relaxed font-normal">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export default CareerStats;
